/**
 * Cart drawer with quantity controls.
 * Toggles .is-open on panel & overlay directly — transitions handled by CSS.
 *
 * Quantity changes update the row optimistically before the network round-trip
 * so rapid clicks compound (without this, a stale data-qty was read off the
 * un-refreshed DOM and multiple +s collapsed into one). Prices and totals are
 * synced from the cart JSON response so the drawer reflects the latest state
 * even if the section endpoint hiccups; we still re-fetch the section after
 * for upsells / ship tracker / empty state.
 */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-panel]');
    this.overlay = this.querySelector('[data-scrim]');
    this.triggerEl = null;

    // Delegated so re-rendered close buttons (empty-state CTA) keep working.
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-close]')) this.close();
    });
    this.overlay?.addEventListener('click', () => this.close());

    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:refresh', () => this.refresh());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
      if (e.key === 'Tab' && this.isOpen) this.#trapFocus(e);
    });

    this.#bindQuantityButtons();
  }

  get isOpen() {
    return this.panel?.classList.contains('is-open');
  }

  open() {
    this.triggerEl = document.activeElement;
    this.panel?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      this.panel.querySelector('[data-close]')?.focus();
    }, 350);

    this.refresh();
  }

  close() {
    if (!this.isOpen) return;

    this.panel?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
    this.triggerEl?.focus();
  }

  #trapFocus(e) {
    const focusable = this.panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  #bindQuantityButtons() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-change-qty]');
      if (!btn) return;
      const line = parseInt(btn.dataset.line, 10);
      const qty = parseInt(btn.dataset.qty, 10);
      if (Number.isNaN(line) || Number.isNaN(qty)) return;
      this.#updateQuantity(line, qty);
    });
  }

  #applyOptimistic(line, quantity) {
    const trigger = this.querySelector(`[data-change-qty][data-line="${line}"]`);
    const row = trigger?.closest('li');
    if (!row) return;
    const qtyEl = row.querySelector('[data-line-qty]');
    if (qtyEl) qtyEl.textContent = String(quantity);
    row.querySelectorAll('[data-change-qty]').forEach((btn) => {
      const action = btn.dataset.action;
      if (action === 'decrement') btn.dataset.qty = String(Math.max(0, quantity - 1));
      else if (action === 'increment') btn.dataset.qty = String(quantity + 1);
    });
  }

  async #updateQuantity(line, quantity) {
    if (quantity > 0) this.#applyOptimistic(line, quantity);

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });

      if (!response.ok) throw new Error('Failed to update cart');

      const cart = await response.json();

      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { item_count: cart.item_count } })
      );

      this.#syncFromCart(cart);

      const status = document.getElementById('cart-status');
      if (status) {
        status.textContent = quantity === 0
          ? 'Item removed from cart.'
          : `Cart updated. ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'} in cart.`;
      }

      await this.refresh();
    } catch (error) {
      console.error('Cart update error:', error);
      // Recover optimistic mistakes by re-pulling the truth.
      this.refresh().catch(() => {});
    }
  }

  /**
   * Update the visible line prices and footer totals from the /cart/change.js
   * response. Keeps the drawer accurate even if the section refresh fails;
   * the section refresh that follows still wins for upsells / tracker / empty.
   */
  #syncFromCart(cart) {
    if (Array.isArray(cart.items)) {
      const rows = this.querySelectorAll('ul[role="list"] > li');
      cart.items.forEach((item, idx) => {
        const row = rows[idx];
        if (!row) return;
        const qtyEl = row.querySelector('[data-line-qty]');
        if (qtyEl) qtyEl.textContent = String(item.quantity);
        const priceEl = row.querySelector('[data-line-price]');
        if (priceEl) {
          // final_line_price carries automatic and code discounts; fall back to
          // line_price for older payloads.
          const finalLine = item.final_line_price ?? item.line_price;
          const nowEl = priceEl.querySelector('[data-line-now]');
          if (nowEl) nowEl.textContent = this.#formatMoney(finalLine, cart.currency);
          else priceEl.textContent = this.#formatMoney(finalLine, cart.currency);

          const wasEl = priceEl.querySelector('[data-line-was]');
          const unit = Number(priceEl.dataset.compareUnit || 0);
          if (wasEl) {
            let was = 0;
            if (unit > item.price) was = unit * item.quantity;
            else if (item.original_line_price > finalLine) was = item.original_line_price;
            wasEl.hidden = was === 0;
            if (was) wasEl.textContent = this.#formatMoney(was, cart.currency);
            priceEl.classList.toggle('is-sale', was > 0);
          }
          const unitEl = priceEl.querySelector('[data-line-unit]');
          if (unitEl) {
            unitEl.hidden = item.quantity < 2;
            if (item.quantity > 1) {
              unitEl.textContent = `${item.quantity} × ${this.#formatMoney(Math.round(finalLine / item.quantity), cart.currency)}`;
            }
          }
        }
        row.querySelectorAll('[data-change-qty]').forEach((btn) => {
          btn.dataset.line = String(idx + 1);
          const action = btn.dataset.action;
          if (action === 'decrement') btn.dataset.qty = String(Math.max(0, item.quantity - 1));
          else if (action === 'increment') btn.dataset.qty = String(item.quantity + 1);
          else if (!action) btn.dataset.qty = '0'; // remove button
        });
      });
    }
  }

  #formatMoney(cents, currency) {
    const code = currency || 'RSD';
    try {
      return new Intl.NumberFormat('sr-RS', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(cents / 100);
    } catch {
      return `${Math.round(cents / 100).toLocaleString('sr-RS')} ${code}`;
    }
  }

  async refresh() {
    try {
      const response = await fetch(`${window.location.pathname}?sections=cart-drawer`);
      const data = await response.json();
      const html = data['cart-drawer'];

      if (html) {
        const fragment = document.createRange().createContextualFragment(html);
        const newContent = fragment.querySelector('[data-cart-drawer-content]');
        const currentContent = this.querySelector('[data-cart-drawer-content]');
        if (newContent && currentContent) {
          currentContent.replaceWith(newContent);
        }
        const newFooter = fragment.querySelector('[data-cart-drawer-footer]');
        const currentFooter = this.querySelector('[data-cart-drawer-footer]');
        if (newFooter && currentFooter) {
          currentFooter.replaceWith(newFooter);
        } else if (newFooter && !currentFooter) {
          this.panel.appendChild(newFooter);
        } else if (!newFooter && currentFooter) {
          currentFooter.remove();
        }
      }
    } catch (error) {
      console.error('Failed to refresh cart drawer:', error);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

export { CartDrawer };
